import { useEffect, useState, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import SEO from "../components/SEO";
import { buildSeoTitle } from "@shared/seoTitle";
import { useSSRData } from "../contexts/SSRDataContext";

interface SiblingNdc {
  ndc: string;
  productId: number;
  strength: string | null;
  dosageForm: string | null;
  manufacturer: string | null;
  inStock: boolean | null;
}

interface NdcDetail {
  ndc: string;
  productId: number;
  name: string;
  genericName: string | null;
  brandName: string | null;
  manufacturer: string | null;
  labelerName: string | null;
  strength: string | null;
  dosageForm: string | null;
  route: string | null;
  packageSize: string | null;
  inStock: boolean | null;
  drugSlug: string | null;
  manufacturerSlug: string | null;
  siblingNdcs: SiblingNdc[];
  education: {
    indicationsAndUsage: string | null;
    warnings: string | null;
  } | null;
}

interface SampleNdc {
  ndc: string;
  name: string;
  genericName: string | null;
}

function truncate(text: string | null | undefined, maxLen: number): string {
  if (!text) return "";
  if (text.length <= maxLen) return text;
  return text.slice(0, maxLen).trimEnd() + "…";
}

function NdcIndexPage({ sampleNdcs }: { sampleNdcs: SampleNdc[] }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    const ndc = query.trim().replace(/\s+/g, "");
    if (ndc) navigate(`/ndc/${encodeURIComponent(ndc)}`);
  }

  return (
    <>
      <SEO
        title={buildSeoTitle("NDC Lookup — Pharmaceutical Drug Identification")}
        description="Look up any NDC (National Drug Code) to identify pharmaceutical products, check wholesale availability, and access DSCSA transaction data at Premier Pharma."
        path="/ndc"
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "NDC Lookup", path: "/ndc" },
        ]}
      />

      <section className="bg-gradient-to-br from-primary via-[#0a1e4a] to-[#0d2d6b] text-white py-14">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <nav aria-label="Breadcrumb" className="text-xs text-white/70 mb-3 flex justify-center gap-2">
            <Link to="/" className="hover:text-white">Home</Link>
            <span>/</span>
            <span className="text-white">NDC Lookup</span>
          </nav>
          <h1 className="text-3xl lg:text-4xl font-bold mb-3">NDC Lookup — Pharmaceutical Drug Identification</h1>
          <p className="text-white/80 text-sm sm:text-base mb-8 max-w-2xl mx-auto">
            Enter an NDC (National Drug Code) to identify the drug, check wholesale availability at Premier Pharma, and access DSCSA transaction information.
          </p>
          <form onSubmit={handleSearch} className="flex gap-2 max-w-lg mx-auto">
            <input
              ref={inputRef}
              type="text"
              aria-label="Enter NDC to look up"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Enter NDC (e.g. 12345-678-90)"
              className="flex-1 bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/50 text-sm focus:outline-none focus:bg-white/20 focus:ring-2 focus:ring-white/60 font-mono"
            />
            <button
              type="submit"
              className="bg-accent hover:bg-blue-600 text-white px-5 py-3 rounded-lg font-medium text-sm transition-colors"
            >
              Look Up
            </button>
          </form>
          <p className="text-white/50 text-xs mt-3">
            NDC codes can be entered with or without dashes (e.g., 00093-7195-56 or 0093719556)
          </p>
        </div>
      </section>

      <section className="py-12 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h2 className="text-lg font-bold text-primary mb-2">What Is an NDC?</h2>
            <p className="text-gray-600 text-sm leading-relaxed mb-4">
              A National Drug Code (NDC) is a unique 10- or 11-digit identifier assigned by the FDA to every commercially available drug product in the United States. It encodes the labeler (manufacturer or repackager), the product (drug name, strength, and dosage form), and the package configuration.
            </p>
            <p className="text-gray-600 text-sm leading-relaxed mb-4">
              NDC codes are printed on every drug package and are used for billing, inventory management, DSCSA track-and-trace, and supply chain verification. Pharmacy staff commonly use NDC lookups to identify products, confirm dosing, and verify supply chain authenticity.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
              {[
                { label: "Labeler Segment", example: "12345", desc: "Identifies the manufacturer or repackager" },
                { label: "Product Segment", example: "678", desc: "Identifies the drug name, strength, and dosage form" },
                { label: "Package Segment", example: "90", desc: "Identifies the package size and type" },
              ].map((seg) => (
                <div key={seg.label} className="bg-gray-50 rounded-lg p-4">
                  <div className="font-mono text-accent text-lg font-bold mb-1">{seg.example}</div>
                  <div className="text-xs font-semibold text-gray-700 mb-1">{seg.label}</div>
                  <div className="text-xs text-gray-500">{seg.desc}</div>
                </div>
              ))}
            </div>
          </div>

          {sampleNdcs.length > 0 && (
            <div>
              <h2 className="text-xl font-bold text-primary mb-4">In-Stock Drugs — Sample NDC Codes</h2>
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">NDC</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Drug Name</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden sm:table-cell">Generic Name</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {sampleNdcs.map((row) => (
                      <tr key={row.ndc} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3">
                          <Link to={`/ndc/${encodeURIComponent(row.ndc)}`} className="font-mono text-accent hover:underline text-xs">
                            {row.ndc}
                          </Link>
                        </td>
                        <td className="px-4 py-3 text-gray-700 text-sm">{row.name}</td>
                        <td className="px-4 py-3 text-gray-500 text-xs hidden sm:table-cell">{row.genericName || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="bg-white border border-gray-200 rounded-2xl p-6 sm:p-8 text-center">
            <h2 className="text-xl font-bold text-primary mb-2">Access Full Wholesale Pricing</h2>
            <p className="text-gray-500 text-sm mb-5 max-w-2xl mx-auto">
              Create a free Premier Pharma account to unlock live wholesale pricing on any NDC, place orders with same-day fulfillment, and receive full DSCSA T2 documentation (TI + TS) and EPCIS interoperability data.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/register" className="bg-accent text-white px-5 py-2.5 rounded-lg font-medium hover:bg-accent/90">Create Account</Link>
              <Link to="/products" className="bg-white border border-gray-300 text-primary px-5 py-2.5 rounded-lg font-medium hover:bg-gray-50">Browse Catalog</Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function NdcDetailPage({ ndc }: { ndc: NdcDetail }) {
  const displayName = ndc.genericName || ndc.name;
  const strengthStr = ndc.strength ? ` ${ndc.strength}` : "";
  const formStr = ndc.dosageForm ? ` ${ndc.dosageForm}` : "";
  const SITE_URL = "https://premierpharma.com";
  const canonical = `${SITE_URL}/ndc/${encodeURIComponent(ndc.ndc)}`;
  const description = `NDC lookup for ${ndc.ndc}: ${displayName}${strengthStr}${formStr}${ndc.manufacturer ? ` by ${ndc.manufacturer}` : ""}. ${ndc.inStock ? "In stock" : "Available for order"} at Premier Pharma wholesale.`;

  const medicalCodeJsonLd: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "MedicalCode",
    codeValue: ndc.ndc,
    codingSystem: "NDC",
    name: displayName,
    url: canonical,
  };

  const productJsonLd: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: ndc.name,
    description,
    sku: ndc.ndc,
    productID: ndc.ndc,
    url: `${SITE_URL}/products/${ndc.productId}`,
    ...(ndc.manufacturer ? {
      brand: { "@type": "Brand", name: ndc.manufacturer },
      manufacturer: { "@type": "Organization", name: ndc.labelerName || ndc.manufacturer },
    } : {}),
  };

  return (
    <>
      <SEO
        title={buildSeoTitle(`NDC ${ndc.ndc}: ${displayName}${strengthStr} — Wholesale Lookup`)}
        description={description}
        path={`/ndc/${encodeURIComponent(ndc.ndc)}`}
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "NDC Lookup", path: "/ndc" },
          { name: `NDC ${ndc.ndc}`, path: `/ndc/${encodeURIComponent(ndc.ndc)}` },
        ]}
        jsonLd={[medicalCodeJsonLd, productJsonLd]}
      />

      <section className="bg-gradient-to-br from-primary via-[#0a1e4a] to-[#0d2d6b] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav aria-label="Breadcrumb" className="text-xs text-white/70 mb-3">
            <Link to="/" className="hover:text-white">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/ndc" className="hover:text-white">NDC Lookup</Link>
            <span className="mx-2">/</span>
            <span className="text-white font-mono">{ndc.ndc}</span>
          </nav>
          <div className="flex items-start gap-4">
            <div>
              <div className="text-white/60 text-xs font-semibold uppercase tracking-wide mb-1">NDC {ndc.ndc}</div>
              <h1 className="text-3xl lg:text-4xl font-bold leading-tight mb-1">
                {displayName}{strengthStr}{formStr}
              </h1>
              {ndc.manufacturer && (
                <p className="text-white/70 text-sm">{ndc.labelerName || ndc.manufacturer}</p>
              )}
              <div className="mt-3">
                <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${ndc.inStock ? "bg-green-500/20 text-green-300" : "bg-blue-500/20 text-blue-300"}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${ndc.inStock ? "bg-green-400" : "bg-blue-400"}`} />
                  {ndc.inStock ? "In Stock at Premier Pharma" : "Available — 2-Day Shipping"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-10 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {[
              { label: "NDC", value: ndc.ndc },
              { label: "Generic Name", value: ndc.genericName || "—" },
              { label: "Brand Name", value: ndc.brandName || "—" },
              { label: "Strength", value: ndc.strength || "—" },
              { label: "Dosage Form", value: ndc.dosageForm || "—" },
              { label: "Route", value: ndc.route || "—" },
              { label: "Package Size", value: ndc.packageSize || "—" },
              { label: "Labeler", value: ndc.labelerName || ndc.manufacturer || "—" },
            ].map((f) => (
              <div key={f.label} className="bg-white border border-gray-200 rounded-xl p-4">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{f.label}</div>
                <div className="text-sm font-medium text-primary font-mono leading-snug">{f.value}</div>
              </div>
            ))}
          </div>

          {ndc.education && (ndc.education.indicationsAndUsage || ndc.education.warnings) && (
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h2 className="text-lg font-bold text-primary mb-4">FDA Drug Information</h2>
              {ndc.education.indicationsAndUsage && (
                <div className="mb-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Indications & Usage</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{truncate(ndc.education.indicationsAndUsage, 500)}</p>
                </div>
              )}
              {ndc.education.warnings && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Important Warnings</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{truncate(ndc.education.warnings, 400)}</p>
                </div>
              )}
              <p className="text-xs text-gray-400 mt-4">Source: FDA. For professional use only. Not a substitute for prescribing information.</p>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to={`/products/${ndc.productId}`}
              className="flex-1 bg-accent text-white rounded-xl p-5 hover:bg-blue-600 transition-colors group"
            >
              <div className="text-sm font-semibold mb-1 group-hover:underline">View Product Detail →</div>
              <div className="text-white/70 text-xs">See full specifications, request 2-day shipping, and create account to view pricing</div>
            </Link>
            {ndc.drugSlug && (
              <Link
                to={`/drugs/${ndc.drugSlug}`}
                className="flex-1 bg-white border border-gray-200 text-primary rounded-xl p-5 hover:border-accent/40 hover:shadow-sm transition-all"
              >
                <div className="text-sm font-semibold mb-1">All {ndc.genericName} Strengths & Forms →</div>
                <div className="text-gray-500 text-xs">Compare all available NDC codes, strengths, and manufacturers</div>
              </Link>
            )}
            {ndc.manufacturerSlug && ndc.manufacturer && (
              <Link
                to={`/manufacturers/${ndc.manufacturerSlug}`}
                className="flex-1 bg-white border border-gray-200 text-primary rounded-xl p-5 hover:border-accent/40 hover:shadow-sm transition-all"
              >
                <div className="text-sm font-semibold mb-1">More from {ndc.manufacturer} →</div>
                <div className="text-gray-500 text-xs">Browse all {ndc.manufacturer} products available at wholesale</div>
              </Link>
            )}
          </div>

          {ndc.siblingNdcs && ndc.siblingNdcs.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                Other {ndc.genericName} NDC Codes
              </h2>
              <div className="space-y-2">
                {ndc.siblingNdcs.map((sib) => (
                  <Link
                    key={sib.ndc}
                    to={`/ndc/${encodeURIComponent(sib.ndc)}`}
                    className="flex items-center justify-between bg-gray-50 hover:bg-blue-50 border border-gray-100 hover:border-accent/30 rounded-lg px-4 py-2.5 transition-colors group"
                  >
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-xs text-accent group-hover:underline">{sib.ndc}</span>
                      {sib.strength && <span className="text-xs text-gray-500">{sib.strength}</span>}
                      {sib.dosageForm && <span className="text-xs text-gray-400">{sib.dosageForm}</span>}
                    </div>
                    <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full flex-shrink-0 ${sib.inStock ? "bg-green-50 text-green-700" : "bg-blue-50 text-blue-600"}`}>
                      <span className={`w-1 h-1 rounded-full ${sib.inStock ? "bg-green-500" : "bg-blue-400"}`} />
                      {sib.inStock ? "In Stock" : "2-Day"}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          <div className="bg-white border border-gray-200 rounded-2xl p-6 sm:p-8 text-center">
            <h2 className="text-xl font-bold text-primary mb-2">Order NDC {ndc.ndc} at Wholesale</h2>
            <p className="text-gray-500 text-sm mb-5 max-w-2xl mx-auto">
              Create a free Premier Pharma account to unlock live wholesale pricing and place orders with same-day fulfillment and full DSCSA T2 documentation (TI + TS).
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/register" className="bg-accent text-white px-5 py-2.5 rounded-lg font-medium hover:bg-accent/90">Create Account</Link>
              <Link to="/ndc" className="bg-white border border-gray-300 text-primary px-5 py-2.5 rounded-lg font-medium hover:bg-gray-50">NDC Lookup</Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default function NdcLookup() {
  const params = useParams<{ ndc?: string }>();
  const ndcParam = params.ndc;
  const { seoData } = useSSRData();

  const [data, setData] = useState<{ type: string; ndc?: NdcDetail; sampleNdcs?: SampleNdc[] } | null>(
    seoData?.type === "ndc-index" || seoData?.type === "ndc-detail" ? seoData : null
  );
  const [loading, setLoading] = useState(!data);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (data && ((!ndcParam && data.type === "ndc-index") || (ndcParam && data.type === "ndc-detail" && data.ndc?.ndc === ndcParam))) {
      return;
    }
    setLoading(true);
    setNotFound(false);
    const url = ndcParam ? `/api/seo/ndc/${encodeURIComponent(ndcParam)}` : "/api/seo/ndc";
    fetch(url)
      .then((r) => {
        if (r.status === 404) { setNotFound(true); return null; }
        if (!r.ok) throw new Error("Failed");
        return r.json();
      })
      .then((d) => { if (d) setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [ndcParam]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-gray-500">Loading…</div>;
  }

  if (notFound) {
    return (
      <>
        <SEO title="NDC Not Found" noindex />
        <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4">
          <h1 className="text-2xl font-bold text-primary mb-2">NDC not found in our catalog</h1>
          <p className="text-gray-500 mb-4">This NDC may not be listed in our current inventory. Try searching the full catalog or contact our team.</p>
          <div className="flex gap-3">
            <Link to="/ndc" className="bg-accent text-white px-5 py-2 rounded-lg font-medium hover:bg-accent/90">NDC Lookup</Link>
            <Link to="/products" className="bg-white border border-gray-300 text-primary px-5 py-2 rounded-lg font-medium hover:bg-gray-50">Browse Catalog</Link>
          </div>
        </div>
      </>
    );
  }

  if (!data) {
    return <div className="min-h-screen flex items-center justify-center text-gray-500">Loading…</div>;
  }

  if (data.type === "ndc-index") {
    return <NdcIndexPage sampleNdcs={data.sampleNdcs || []} />;
  }

  if (data.type === "ndc-detail" && data.ndc) {
    return <NdcDetailPage ndc={data.ndc} />;
  }

  return null;
}
